import { Component, TemplateRef, ViewChild, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { UserService } from '../services/user.service';
import { DatabaseService } from '../services/database.service';
import { ColorService } from '../services/color.service';
import { AlertService } from '../services/alert.service';
import { OptionsService } from '../services/options.service';
import { EncodingService } from '../services/encoding.service';
import { MatDialog } from '@angular/material/dialog';
import { UserSelectionDialogComponent } from '../user-selection-dialog/user-selection-dialog.component';

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss'],
  standalone: false
})
export class AdminComponent implements AfterViewInit {
  user: any;
  allAdmins: any[] = [];
  searchResults: any[] = [];
  searchValue: string = '';
  loading = true;
  adminLoadError: string | null = null; // capture backend load error
  searching = false;
  addingGmid: string | null = null;
  removingGmid: string | null = null;
  allOptions: any;
  selectedOptionName: string = '';
  selectedOptionDetails: any = null;
  optionNames: string[] = [];
  newOptionKey: string = '';
  newOptionValue: string = '';
  addingNewOption: boolean = false;
  removingOptionKey: string = '';
  holidays: { date: Date | null; id: string | '' }[] = [{ date: null, id: '' }];
  // Inline top-level holiday add form state
  newHolidayDate: Date | null = null;
  addingHoliday: boolean = false;
  viewers: any;
  searchName: string = '';
  searchingForPersonLoading: boolean = false;

  // Add this property to track which admin is being updated
  updatingSchedulingTeam: string | null = null;
  updatingOwner: string | null = null; // track owner update in progress

  editingColorGmid: string | null = null;
  colorInputs: { [key: string]: string } = {}; // Store color input values for each user
  unsyncedColors: Set<string> = new Set(); // GMIDs whose color hasn't been confirmed from backend

  // Dialog scroll shadow state
  dialogScrollTopActive = false;
  dialogScrollBottomActive = false;

  // Dialog template refs
  @ViewChild('optionsDialogTemplate', { static: false }) optionsDialogTemplate!: TemplateRef<any>;
  @ViewChild('holidayDialogTemplate', { static: false }) holidayDialogTemplate!: TemplateRef<any>;
  @ViewChild('viewerDialogTemplate', { static: false }) viewerDialogTemplate!: TemplateRef<any>;

  constructor(private _user: UserService,
    private _db: DatabaseService,
    private _color: ColorService,
    private _alert: AlertService,
    private _options: OptionsService,
  private _encoding: EncodingService,
  private _dialog: MatDialog,
  private _cdr: ChangeDetectorRef) {

  }

  ngOnInit() {
    this.fetchAdmins();
    this._user.user$.subscribe(user => {
      if (user && user !== null) {
        this.user = user;
        // this.isAdmin = user.isAdmin || false;
      }
    });

    this.loadAllOptions();
    this._db.getHolidayEvents().subscribe({
      next: (res) => {
        const currentYear = new Date().getFullYear();
        if (Array.isArray(res) && res.length > 0) {
          this.holidays = res
            .filter((h: any) => {
              if (!h.date) return false;
              const holidayYear = new Date(h.date).getFullYear();
              return holidayYear >= currentYear;
            })
            .map((h: any) => ({
              date: h.date ? new Date(h.date.replace("T00", "T12")) : null,
              id: h.id || ''
            }));
          this.holidays.push({ date: null, id: '' });
          console.log('holidays', this.holidays)
        } else {
          this.holidays = [{ date: null, id: '' }];
        }
      },
      error: (error) => {
        console.error('Error loading holiday events:', error);
      }
    });

    this._db.getAllViewers().subscribe({
      next: (res) => {
        console.log('Viewers loaded:', res);
        this.viewers = res || [];
      }, 
      error: (error) => {
        console.error('Error loading viewers:', error);
      }
    })
  }

  ngAfterViewInit(): void {
    // Defer logging to ensure change detection completed
    setTimeout(() => {
      console.log('[Admin] Dialog templates resolved:', {
        options: !!this.optionsDialogTemplate,
        holidays: !!this.holidayDialogTemplate,
        viewers: !!this.viewerDialogTemplate
      });
    });
  }
  addHoliday(index: number) {
    const selectedDate = this.holidays[index].date;
    if (!selectedDate) {
      this._alert.error('Please select a date before adding a new holiday.');
      return;
    }
    this.loading = true;
    const formattedDate = selectedDate instanceof Date
      ? selectedDate.toISOString().split('T')[0]
      : selectedDate;

    this._db.addHolidayEvent({ date: formattedDate }).subscribe({
      next: (res) => {
        // Update the current index with the id from the response
        if (res && res.id) {
          this.holidays[index].id = res.id;
        }
        // Only add a new blank row if there isn't one already
        const hasBlank = this.holidays.some(h => !h.date && !h.id);
        if (!hasBlank) {
          this.holidays.push({ date: null, id: '' });
        }
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        console.error('Error adding holiday event:', error);
      }
    });
  }

  // Add holiday from the new top inline control
  addHolidayTop() {
    if (!this.newHolidayDate) {
      this._alert.error('Select a date first.');
      return;
    }
    // Prevent duplicates (compare YYYY-MM-DD)
    const formattedDate = this.newHolidayDate instanceof Date
      ? this.newHolidayDate.toISOString().replace("T00", "T12")
      : this.newHolidayDate;
    const exists = this.holidays.some(h => h.date && (h.date instanceof Date ? h.date.toISOString().split('T')[0] : h.date) === formattedDate);
    if (exists) {
      this._alert.warning('Holiday already exists for that date.');
      return;
    }
    this.addingHoliday = true;
    this._db.addHolidayEvent({ date: formattedDate }).subscribe({
      next: (res) => {
        if (res && res.id) {
          this.holidays.push({ date: new Date(formattedDate), id: res.id });
          // Sort by date ascending
          this.holidays.sort((a, b) => {
            const at = a.date ? (a.date instanceof Date ? a.date.getTime() : new Date(a.date).getTime()) : 0;
            const bt = b.date ? (b.date instanceof Date ? b.date.getTime() : new Date(b.date).getTime()) : 0;
            return at - bt;
          });
        }
        this.newHolidayDate = null;
        this.addingHoliday = false;
      },
      error: (error) => {
        console.error('Error adding holiday event:', error);
        this.addingHoliday = false;
      }
    });
  }

  removeHoliday(index: number) {
    const holidayId = this.holidays[index].id;
    if (!holidayId) {
      this.holidays.splice(index, 1);
      return;
    }
    this.loading = true;
    this._db.deleteHolidayEvent(holidayId).subscribe({
      next: () => {
        this.holidays.splice(index, 1);
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        console.error('Error deleting holiday event:', error);
      }
    });
  } loadAllOptions() {
    this._options.getAllOptions().subscribe({
      next: (options) => {
        this.allOptions = options;
        this.optionNames = options.map((opt: any) => opt.name);
      },
      error: (error) => {
        console.error('Error loading options:', error);
      }
    });
  }

  /**
   * Add a new viewer based on the GMID input at index i
   * @param i Index of the viewer in the viewers array
   * @returns 
   */
  addViewer(personObj: any) {
    const gmid = personObj.gmid;
    if (!gmid || gmid.trim() === '') {
      this._alert.error('Please enter a valid GMID.');
      return;
    }
    this.loading = true;
    this._db.addViewer(personObj).subscribe({
      next: (res) => {
        this.viewers.push(res);
        this.loading = false;
      }, error: (error) => {
        this.loading = false;
        console.error('Error adding viewer:', error);
        this._alert.error('Failed to add viewer. Please try again.', this._alert.getErrorDetails(error));
      }
    });
  }

  /**
   * Remove a viewer based on the GMID input at index i
   * @param i Index of the viewer in the viewers array
   * @returns 
   */
  removeViewer(i: any) {
    const gmid = this.viewers[i].gmid;
    if (!gmid || gmid.trim() === '') {
      this._alert.error('Invalid GMID.');
      return;
    }
    this.loading = true;
    this._db.removeViewer(gmid.trim()).subscribe({
      next: () => {
        this.viewers = this.viewers.filter((v: any) => v.gmid !== gmid);
        this.loading = false;
      }, error: (error) => {
        this.loading = false;
        console.error('Error removing viewer:', error);
        this._alert.error('Failed to remove viewer. Please try again.', this._alert.getErrorDetails(error));
      }
    });
  }

  searchForPerson() {
    const person = this.searchName.trim();
    if (!person || person === '') {
      this._alert.error('Please enter a name or email to search.');
      return;
    }
    this.searchingForPersonLoading = true;
    this._user.searchPerson(person).subscribe((response: any) => {
      // console.log('Graph API Response:', response);
      if (response && response.length > 0) {
        const dialogRef = this._dialog.open(UserSelectionDialogComponent, {
          width: '500px',
          data: { users: response }
        });
        this.searchingForPersonLoading = false;

        dialogRef.afterClosed().subscribe((selectedUser) => {
          if (selectedUser) {
            // this.searchUser = selectedUser;
            console.log('Selected user:', selectedUser);
            let personObj = {
              gmid: selectedUser.userPrincipalName.split('@')[0].toLowerCase(),
              name: selectedUser.displayName || '',
              email: selectedUser.mail || ''
            };
            this.addViewer(personObj);
            this.searchName = '';
            // this._user.getOtherGraphProfilePhoto(selectedUser.id).subscribe(res => {
            //   console.log("Get graph profile", res)
            //   if (res) {
            //     this.requestorPicture = res;
            //   } else {
            //     this.requestorPicture = '';
            //   }
            // });
            // this.isRequestorLinkVisible = true;
          }
        });
      } else {
        console.error('No matching user found');
        this.searchingForPersonLoading = false;
      }
    });
  }

  fetchAdmins() {
    this.adminLoadError = null;
    this.loading = true;
    this._db.getAllAdmins().subscribe({
      next: (res: any) => {
        this.allAdmins = (res?.admins || []).map((a: any) => {
          const rawColor = a.preferredColor || a.preferredcolor || a.preferred_color;
          if (rawColor) a.preferredColor = rawColor;
          return a;
        });
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        console.error('Failed to load admins', err);
        this.adminLoadError = err.error?.detail || err.error?.error || err.message || 'Failed to load admins';
        this.loading = false;
      }
    });
  }

  // trackBy helpers to optimize *ngFor and preserve identity
  trackPerson = (_: number, person: any) => person?.onPremisesSamAccountName || person?.gmid || person?.email;
  trackAdmin = (_: number, admin: any) => admin?.gmid || admin?.email;

  search() {
    this.searching = true;
    this._user.searchForPersonByName(this.searchValue).subscribe((res: any) => {
      // console.log(res);
      if (res) {
        this.searchResults = res;
        // Remove any results that are already in allAdmins or account is disabled or gmid is null
        this.searchResults = this.searchResults.filter((result: any) => {
          const exists = this.allAdmins.some(admin => admin.gmid === result.onPremisesSamAccountName);

          return !exists && result.accountEnabled && result.onPremisesSamAccountName !== null;
        });
      } else {
        console.error('No results found');
      }
      this.searching = false;
    });
  }

  async addAdmin(person: any) {
    if (this.loading) return;
    this.addingGmid = person.onPremisesSamAccountName;
    // Check to make sure admin is not already in allAdmins
    const exists = this.allAdmins.some(admin => admin.gmid === person.onPremisesSamAccountName);
    if (exists) {
      await this._alert.warning('Admin already exists');
      this.addingGmid = null;
      return;
    }

    const adminData = {
      name: person.displayName,
      gmid: person.onPremisesSamAccountName,
      email: person.mail,
      gmin: person.employeeId
    };
    this._db.addAdmins(adminData).subscribe((res: any) => {
      this.allAdmins.push(res.admin);
      this.searchResults = this.searchResults.filter(result => result.onPremisesSamAccountName !== person.onPremisesSamAccountName);
      this.addingGmid = null;
    }, () => {
      this.addingGmid = null;
    });
  }

  removeAdmin(gmid: string) {
    if (this.removingGmid) return;
    this.removingGmid = gmid;
    this._db.removeAdmin(gmid).subscribe({
      next: () => {
        this.allAdmins = this.allAdmins.filter(admin => admin.gmid !== gmid);
        this.removingGmid = null;
      },
      error: async (error) => {
        this.removingGmid = null;
        await this._alert.error('Failed to remove admin.', this._alert.getErrorDetails(error));
      }
    });
  }


  /**
 * Update the scheduling team status for an admin
 * @param admin The admin object to update
 * @param isSchedulingTeam The new scheduling team status
 */
  updateSchedulingTeam(admin: any, isSchedulingTeam: boolean): void {
    this.updatingSchedulingTeam = admin.gmid;

    // Update the admin object locally first for immediate UI feedback
    admin.isSchedulingTeam = isSchedulingTeam;

    // Call your backend API to update the admin
    this._db.updateAdminSchedulingTeam(admin.gmid, isSchedulingTeam).subscribe({
      next: (response) => {
        // console.log('Scheduling team status updated successfully:', response);
        this.updatingSchedulingTeam = null;
        // Optionally refresh the admin list
        // this.getAllAdmins();
      },
      error: async (error) => {
        console.error('Error updating scheduling team status:', error);
        // Revert the local change on error
        admin.isSchedulingTeam = !isSchedulingTeam;
        this.updatingSchedulingTeam = null;
        await this._alert.error('Error updating scheduling team status. Please try again.', this._alert.getErrorDetails(error));
      }
    });
  }
  /**
   * Update owner status for an admin
   * @param admin Admin object to update
   * @param isOwner new owner flag
   */
  updateOwnerStatus(admin: any, isOwner: boolean): void {
    this.updatingOwner = admin.gmid;
    const previous = admin.isOwner;
    admin.isOwner = isOwner; // optimistic
    this._db.updateAdminOwner(admin.gmid, isOwner).subscribe({
      next: () => {
        this.updatingOwner = null;
      },
      error: async (err) => {
        console.error('[Owner Update] Failed to update owner status:', {
          status: err?.status,
          statusText: err?.statusText,
          message: err?.message,
          url: err?.url,
          errorBody: err?.error
        });
        admin.isOwner = previous; // revert
        this.updatingOwner = null;
        const status = err?.status;
        let msg = 'Error updating owner status.';
        if (status === 404) {
          msg += ' (Endpoint not found: /api/admin/owner). Verify backend route & method.)';
        } else if (status === 403) {
          msg += ' (Forbidden – missing permission.)';
        } else if (status === 0) {
          msg += ' (Network error – server unreachable.)';
        }
        await this._alert.error(msg, this._alert.getErrorDetails(err));
      }
    });
  }
  // Initialize color input for a user
  initColorInput(gmid: string, currentColor?: string) {
    // Get the current saved color or use the provided one
    const savedColor = this.getCurrentSavedColor(gmid);
    this.colorInputs[gmid] = currentColor || savedColor;
    this.editingColorGmid = gmid;
    // console.log(`Initializing color input for ${gmid} with color: ${this.colorInputs[gmid]}`);
  }

  // Validate hex color format
  isValidHexColor(hex: string): boolean {
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexRegex.test(hex);
  }

  // Handle color picker change
  onColorPickerChange(gmid: string, event: any): void {
    const color = event.target.value;
    // console.log(`Color picker changed for ${gmid}: ${color}`);
    this.colorInputs[gmid] = color;
  }

  // Handle hex input change with validation
  onHexInputChange(gmid: string, event: any): void {
    let hexValue = event.target.value;
    // console.log(`Hex input changed for ${gmid}: ${hexValue}`);

    // Auto-add # if not present and looks like hex
    if (hexValue && !hexValue.startsWith('#') && /^[A-Fa-f0-9]{3,6}$/.test(hexValue)) {
      hexValue = '#' + hexValue;
      this.colorInputs[gmid] = hexValue;
    }
  }

  // Update user color preference
  async updateUserColor(gmid: string): Promise<void> {
    const color = this.colorInputs[gmid];
    if (!this.isValidHexColor(color)) {
      await this._alert.error('Please enter a valid hex color (e.g., #FF0000)');
      return;
    }

    // console.log(`Attempting to update color for ${gmid} to ${color}`);

    // Save to local storage immediately
    // this.saveColorToLocalStorage(gmid, color);
    this.editingColorGmid = null;
    // this.unsyncedColors.add(gmid);

    // Notify other components about the color change IMMEDIATELY
    // console.log('Notifying ColorService about color update...');
    this._color.notifyColorUpdate(gmid, color);

    // Try to update via API (but don't wait for it)
    this._color.updateUserColor(gmid, color).subscribe({
      next: async (response: any) => {
        // console.log('Color updated successfully via API:', response);
        await this._alert.success(`Color updated successfully! The new color "${color}" has been saved and is now active.`);

        // Notify again after API success to ensure dashboard gets the update
        this._color.notifyColorUpdate(gmid, color);

        // Sync in-memory admin list with preferredColor so subsequent loads show without localStorage
        const admin = this.allAdmins.find(a => a.gmid === gmid);
        if (admin) {
          admin.preferredColor = color;
        }
      },
      error: async (error: any) => {
        console.error('API error', error);
        await this._alert.error(`Update Color failed! (API Error: ${error.status})`, this._alert.getErrorDetails(error));
      }
    });
  }

  // Cancel color editing
  cancelColorEdit() {
    this.editingColorGmid = null;
    this.colorInputs = {};
  }

  // Get current saved color for display
  getCurrentSavedColor(gmid: string): string {
    try {
      // Prefer in-memory admin preferredColor if present (reflects DB / last update)
      const admin = this.allAdmins.find(a => a.gmid === gmid);
      if (admin) {
        const rawColor = admin.preferredColor || admin.preferredcolor || admin.preferred_color;
        if (rawColor) return rawColor;
      }
      const colors = JSON.parse(localStorage.getItem('userColors') || '{}');
      return colors[gmid] || '';
    } catch (error) {
      // TEMP TEST MODE: no fallback color
      return '';
    }
  }

  isColorUnsynced(gmid: string): boolean {
    return this.unsyncedColors.has(gmid);
  }

  // Handle option selection from dropdown
  onOptionSelected(optionName: string): void {
    this.selectedOptionName = optionName;

    const option = this.allOptions.find((opt: any) => opt.name === optionName);
    this.newOptionKey = '';
    this.newOptionValue = '';
    this.addingNewOption = false;
    this.removingOptionKey = '';

    if (option) {
      this.selectedOptionDetails = option;
      if (!this.selectedOptionDetails.isObject && typeof this.selectedOptionDetails.value === 'string') {
        this.selectedOptionDetails.value = JSON.parse(this.selectedOptionDetails.value).arr.split(',');
      }
      console.log('Selected option details:', this.selectedOptionDetails);
    }
  }

  // Get the parsed options as an object for editing
  getParsedOptions() {
    if (!this.selectedOptionDetails || !this.selectedOptionDetails.value) {
      return {};
    }

    try {
      // Use your existing JSON parsing logic
      return this.parseOptionsValue(this.selectedOptionDetails.value);
    } catch (error) {
      console.error('Error parsing options:', error);
      return {};
    }
  }

  // Parse options value (similar to your existing translateOptionsForUse logic)
  parseOptionsValue(value: string): { [key: string]: string } {
    try {
      let jsonString = value;

      // Fix malformed JSON if needed
      if (!jsonString.startsWith('{"') && jsonString.startsWith('{')) {
        jsonString = jsonString
          .replace(/([{,]\s*)([^"{\s][^:,}]*?)(\s*):/g, '$1"$2":')
          .replace(/:(\s*)([^",}\s][^,}]*?)(\s*)([,}])/g, ':"$2"$4');
      }

      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Error parsing options value:', error);
      return {};
    }
  }

  // Add a new option to the selected option set
  addNewOption(): void {
    if (!this.newOptionValue || !this.selectedOptionDetails) {
      return;
    }
    
    this.addingNewOption = true;
    let updatedValue = null;

    if (!this.selectedOptionDetails.isObject) {
      if (this.selectedOptionDetails.value.includes(this.newOptionValue)) {
        console.error('Option value already exists in the list.');
        this.addingNewOption = false;
        return;
      }
      this.selectedOptionDetails.value.push(this.newOptionValue);
      // sort into alphabetical order
      this.selectedOptionDetails.value.sort((a: string, b: string) => a.localeCompare(b));
      updatedValue = { arr: this.selectedOptionDetails.value.join(',') };
    } else {
      if (!this.selectedOptionDetails.prefix || this.selectedOptionDetails.prefix == '') {
        console.error('Cannot add new option key/value without a standard prefix defined on the option set.');
        return;
      }

      this.newOptionKey = this.selectedOptionDetails.prefix + this.newOptionValue.replace(/\s+/g, '');

      const currentOptions = this.getParsedOptions();
      currentOptions[this.newOptionKey] = this.newOptionValue;

      updatedValue = JSON.stringify(currentOptions);
      this.selectedOptionDetails.value = updatedValue;
    }

    if (!updatedValue) {
      console.error('Updated value never set, must be an error');
      return;
    }

    // Call your backend API to update the option
    this._options.updateOption(this.selectedOptionDetails.id, updatedValue).subscribe({
      next: (response) => {
        console.log('Option updated successfully:', response);
        this.newOptionKey = '';
        this.newOptionValue = '';
        this.addingNewOption = false;
      },
      error: (error) => {
        console.error('Error updating option:', error);
        console.error('Full error details:', {
          status: error.status,
          statusText: error.statusText,
          error: error.error,
          message: error.message
        });
        this.addingNewOption = false;
      }
    });
  }

  // Remove an option from the selected option set
  removeOption(keyToRemove: string): void {
    if (!this.selectedOptionDetails) {
      return;
    }

    let updatedValue = null;

    this.removingOptionKey = keyToRemove;

    if (!this.selectedOptionDetails.isObject) {
      this.selectedOptionDetails.value = this.selectedOptionDetails.value.filter((val: string) => val !== keyToRemove);
      updatedValue = { arr: this.selectedOptionDetails.value.join(',') };
    } else {

      const currentOptions = this.getParsedOptions();
      delete currentOptions[keyToRemove];

      updatedValue = JSON.stringify(currentOptions);
      this.selectedOptionDetails.value = updatedValue;
    }

    if (!updatedValue) {
      console.error('Updated value never set, must be an error');
      return;
    }

    // Call your backend API to update the option
    this._options.updateOption(this.selectedOptionDetails.id, updatedValue).subscribe({
      next: (response) => {
        console.log('Option removed successfully:', response);
        this.removingOptionKey = '';
      },
      error: (error) => {
        console.error('Error removing option:', error);
        this.removingOptionKey = '';
      }
    });
  }

  // Get all option keys for display
  getOptionKeys(): string[] {
    return Object.keys(this.getParsedOptions());
  }

  // ----- Dialog Helpers -----
  openOptionsDialog(): void {
    if (!this.optionsDialogTemplate) {
      console.error('[Admin] optionsDialogTemplate not yet available (clicked before view init?).');
      this._cdr.detectChanges();
      if (!this.optionsDialogTemplate) return;
    }
    this._dialog.open(this.optionsDialogTemplate, {
  width: '1000px',
  maxWidth: '96vw',
  maxHeight: '92vh',
      autoFocus: true,
      panelClass: 'admin-dialog-panel'
    });
  }

  openHolidayDialog(): void {
    if (!this.holidayDialogTemplate) {
      console.error('[Admin] holidayDialogTemplate not yet available (clicked before view init?).');
      this._cdr.detectChanges();
      if (!this.holidayDialogTemplate) return;
    }
    this._dialog.open(this.holidayDialogTemplate, {
  width: '780px',
  maxWidth: '96vw',
  maxHeight: '92vh',
      autoFocus: true,
      panelClass: 'admin-dialog-panel'
    });
  }

  openViewerDialog(): void {
    if (!this.viewerDialogTemplate) {
      console.error('[Admin] viewerDialogTemplate not yet available (clicked before view init?).');
      this._cdr.detectChanges();
      if (!this.viewerDialogTemplate) return;
    }
    this._dialog.open(this.viewerDialogTemplate, {
      width: '720px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      autoFocus: true,
      panelClass: 'admin-dialog-panel'
    });
  }

  onDialogScroll(evt: Event): void {
    const el = evt.target as HTMLElement;
    if (!el) return;
    const atTop = el.scrollTop <= 0;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 1;
    this.dialogScrollTopActive = !atTop;
    this.dialogScrollBottomActive = !atBottom;
  }

}
